function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Button } from "@progress/kendo-react-buttons";
import { PageHeader } from "../../components/analytics-kit";
import { INPUT } from "../../components/form-styles";
import { runAgent, waitAgent, runIdOf } from "./agent-run";
import { AssetPicker } from "./sequence-create";
import { CLIP_LENGTHS, shotsFor, lengthForLines, ClipNewDirect, ClipNewUpload } from "./clip-new";
import ClipEditor from "./clip-editor";
import * as api from "./sequences-api";
import endpoints from "../../endpoints";
import { call } from "../audiences/api";
import { VIDEO_QUALITIES, lastVideoQuality, rememberVideoQuality, qualityNote } from "./video-quality";

/*
 * Clip workspace (DS 2026-10-07: "go ahead and build it to staging"). The video counterpart of the image workspace.
 * Routes: /Content/clips/new (start: direct a clip from a product image, or upload a video) and /Content/clips/:id.
 *
 * One page per video family. Redesigned 2026-10-08 (PlunkDocs mockups/video-editors-redesign-2026-10-08.html): the editor
 * itself lives in clip-editor.jsx (top bar · player on a quiet stage · timeline with trim handles · prompt bar to extend ·
 * versions filmstrip · Edit / Sizes / Details panel). This file keeps the routes, the data load, the history rows and the
 * directed-clip form. Every edit works on the selected version and lands as a new version, which is then selected. No backend of its own: GET /content/assets/{id}/versions,
 * GET and POST /content/assets/{id}/edits. A directed clip keeps its recipe as a clip_render edit row (params), so
 * Re-direct reopens the form with the same image, shots, length and format and saves the result as a version.
 * No credit amounts anywhere; the only credit message is the insufficient-credits notice with the purchase path.
 */

const ROOT = String(endpoints.product && endpoints.product.root || "");
const CONTENT = `${ROOT}/content`;
const BUY_URL = "https://account.plunk.io/?from=app";
const toast = api.toast;
const idStr = v => v === undefined || v === null ? "" : String(v);
const items = d => Array.isArray(d) ? d : d && (d.items || d.Items) || [];
async function safe(p, fb) {
  try {
    const r = await p;
    return r === undefined || r === null ? fb : r;
  } catch (e) {
    return fb;
  }
}

// 8 s opening shot, then 7 s per directed extension (8 + 7n, up to 148 s); eight direction lines, the last repeats.
// The constants live in clip-new.jsx (the new-clip page) and are re-exported here for existing importers.
export { CLIP_LENGTHS, shotsFor };
const MAX_SHOT_LINES = 8;
const MAX_KEEP = 3;
const SHOT_PLACEHOLDERS = ["Slow push in, soft morning light", "Camera drifts slowly to the left", "Settle on a wide shot", "Close-up on the details", "Orbit a quarter turn, keep it centered", "Tilt down slowly", "Pull back to show the whole scene", "Hold on the best angle"];
const ACTION_LABEL = {
  clip_render: "Directed clip",
  trim: "Trim",
  extend: "Extend",
  extend_video: "Extended",
  video_edit: "Cut and caption",
  video_resize: "Resize",
  clip: "Clip",
  upload: "Upload"
};
const actionLabel = a => ACTION_LABEL[a] || String(a || "Edit").replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());
const parseJson = s => {
  try {
    return typeof s === "string" ? JSON.parse(s) : s || null;
  } catch (e) {
    return null;
  }
};
const norm = a => a ? {
  ...a,
  id: idStr(a.id || a.Id),
  url: a.url || a.Url,
  title: a.title || a.Title,
  posterUrl: a.posterUrl || a.PosterUrl,
  durationSeconds: a.durationSeconds != null ? a.durationSeconds : a.DurationSeconds,
  version: Number(a.version || a.Version || 1),
  parentAssetId: a.parentAssetId || a.ParentAssetId || null,
  createdDate: a.createdDate || a.CreatedDate,
  width: a.width || a.Width,
  height: a.height || a.Height,
  kind: a.kind || a.Kind,
  source: a.source || a.Source
} : null;

/**
 * The directed-clip form: one product image (first frame), up to three images that keep the product consistent,
 * length, format and one direction line per shot. `versionOf` saves the render as a version of that family;
 * otherwise it creates a new video asset and logs the recipe on it. Calls onDone(assetId) with the asset to open.
 *
 * Two layouts over the same state and pipeline: the default compact panel (the clip editor's Re-direct tool) and
 * layout="page" (the full /Content/clips/new page, drawn by ClipNewDirect in clip-new.jsx; extra props onBack and
 * onUploadMode). On the page the length follows the number of written lines (8 s plus 7 s per extra line, snapped up to
 * CLIP_LENGTHS) unless "Make it longer" picks more; square stays 8 s only.
 * The asset is created by this browser after the render finishes (POST /content/assets after waitAgent), so the page
 * has to stay open while it renders; the copy says so.
 */
export class ClipForm extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", (() => {
      const r = this.props.recipe || {};
      return {
        refId: idStr(r.imageAssetId || this.props.refId || ""),
        keepIds: (r.keepAssetIds || []).map(idStr),
        assets: [],
        aspect: r.aspect || "16:9",
        length: Number(r.lengthSeconds) || 8,
        shots: r.shots && r.shots.length ? r.shots : [""],
        title: "",
        busy: false,
        progress: "",
        credits: null,
        longer: 0,
        assetsLoaded: false,
        quality: r.quality === "best" || r.quality === "standard" ? r.quality : lastVideoQuality()
      };
    })());
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "assetUrl", id => {
      const a = (this.state.assets || []).find(x => idStr(x.id) === idStr(id));
      return a ? a.latestUrl || a.url || "" : "";
    });
    _defineProperty(this, "setShot", (i, v) => this.setState({
      shots: this.state.shots.map((s, k) => k === i ? v : s)
    }));
    _defineProperty(this, "addShot", () => {
      if (this.state.shots.length < MAX_SHOT_LINES) this.setState({
        shots: [...this.state.shots, ""]
      });
    });
    _defineProperty(this, "removeShot", i => {
      const shots = this.state.shots.filter((_, k) => k !== i);
      this.setState({
        shots: shots.length ? shots : [""]
      });
    });
    _defineProperty(this, "setQuality", q => {
      rememberVideoQuality(q);
      this.setState({
        quality: q
      });
    });
    _defineProperty(this, "setLength", len => {
      const patch = {
        length: len
      };
      if (len > 8 && this.state.aspect === "1:1") patch.aspect = "16:9";
      this.setState(patch);
    });
    _defineProperty(this, "make", async () => {
      const {
        shots,
        refId,
        keepIds,
        title
      } = this.state;
      const length = this.effLength();
      const aspect = this.state.aspect === "1:1" && length > 8 ? "16:9" : this.state.aspect;
      const imageUrl = this.assetUrl(refId) || this.props.recipe && this.props.recipe.imageUrl || "";
      const keepUrls = keepIds.map(id => this.assetUrl(id)).filter(Boolean);
      if (!imageUrl) {
        toast("Pick the image the clip starts from.");
        return;
      }
      const lines = shots.map(s => s.trim()).filter(Boolean);
      const n = shotsFor(length);
      this.setState({
        busy: true,
        credits: null,
        progress: n > 1 ? `Shot 1 of ${n}` : ""
      });
      if (this.props.onBusy) this.props.onBusy(true);
      try {
        const lead = lines[0] || "slow camera move, natural light";
        const text = `${lead}. Cinematic product shot: the product exactly as in the reference image, no text.`;
        const quality = this.state.quality || lastVideoQuality();
        const params = {
          imageUrl,
          aspectRatio: aspect,
          prompt: text,
          lengthSeconds: String(length),
          quality
        };
        if (lines.length) params.shots = JSON.stringify(lines);
        if (keepUrls.length) params.referenceImageUrls = JSON.stringify(keepUrls);
        const r = await runAgent("content_cinematic_agent", text, params);
        const recipe = {
          imageAssetId: refId,
          imageUrl,
          keepAssetIds: keepIds,
          keepUrls,
          aspect,
          lengthSeconds: length,
          shots: lines,
          quality
        };
        // A new clip is registered with the server right away (POST /content/clips): the server folds the finished
        // render into Assets even if this page is closed. Older servers without the route fall back to saving here.
        let pendingId = "";
        if (!this.props.versionOf) {
          const reg = await safe(call(`${CONTENT}/clips`, {
            method: "POST",
            body: {
              runId: runIdOf(r),
              title: title.trim() || `Clip · ${(lines[0] || "directed").slice(0, 48)}`,
              recipe: JSON.stringify(recipe),
              prompt: lines[0] || "",
              lengthSeconds: length
            }
          }), null);
          pendingId = idStr(reg && reg.id);
        }
        toast(pendingId ? n > 1 ? `Rendering ${n} shots: several minutes. You can leave this page; the clip appears in Assets when it's done.` : "Rendering: a few minutes. You can leave this page; the clip appears in Assets when it's done." : n > 1 ? `Rendering ${n} shots: several minutes. Keep this page open.` : "Rendering: a few minutes. Keep this page open.");
        if (this._alive) this.setState({
          pendingId
        });
        const done = await waitAgent(runIdOf(r), 15000, Math.min(240, 60 + n * 24), () => this._alive, run => {
          let ctx = {};
          try {
            ctx = JSON.parse(run.contextOutput || run.ContextOutput || "{}");
          } catch (e) {/* no context yet */}
          const cur = Number(ctx.clipShot || 0),
            total = Number(ctx.clipShots || n);
          if (this._alive && cur > 0) this.setState({
            progress: `Shot ${cur} of ${total}`
          });
        });
        if (pendingId) {
          if (!this._alive) return;
          if (done.ok) {
            const st = await safe(call(`${CONTENT}/clips/${encodeURIComponent(pendingId)}`), null);
            if (st && st.status === "failed") {
              toast(st.error || "The clip could not be rendered. Try other directions or another image.");
              return;
            }
            toast("Clip ready.");
            if (this.props.onDone) this.props.onDone(pendingId);
          } else toast(done.timeout ? "Still rendering. It appears in Assets when it's done, even if you leave this page." : "The clip could not be rendered. Try other directions or another image.");
          return;
        }
        const url = done.ok && (done.ctx && (done.ctx.videoUrl || done.ctx.url) || done.res && (done.res.videoUrl || done.res.url));
        if (!url) {
          toast(done.timeout ? "Still rendering, but this server cannot save it later. Make the clip again with this page open." : "The clip could not be rendered. Try other directions or another image.");
          return;
        }
        const meta = (done.ctx && done.ctx.posterUrl ? done.ctx : done.res) || {};
        const label = `Directed · ${(lines[0] || "clip").slice(0, 40)}`;
        const dur = meta.durationSeconds || meta.duration || meta.seconds || length;
        if (this.props.versionOf) {
          const saved = await call(`${CONTENT}/assets/${encodeURIComponent(this.props.versionOf)}/edits`, {
            method: "POST",
            body: {
              action: "clip_render",
              params: JSON.stringify(recipe),
              prompt: lead,
              status: "done",
              url,
              posterUrl: meta.posterUrl || null,
              durationSeconds: Math.round(Number(dur) || 0),
              label,
              credits: 0
            }
          });
          const v = saved && (saved.version || saved.Version);
          toast(v ? `Saved as v${v.version || v.Version}.` : "Saved as a new version.");
          if (this.props.onDone) this.props.onDone(v ? idStr(v.id || v.Id) : this.props.versionOf);
        } else {
          const a = await call(`${CONTENT}/assets`, {
            method: "POST",
            body: {
              kind: "video",
              url,
              title: title.trim() || `Clip · ${(lines[0] || "directed").slice(0, 48)}`,
              source: "plunk_cinematic",
              posterUrl: meta.posterUrl || undefined,
              durationSeconds: Math.round(Number(dur) || 0) || undefined
            }
          });
          const id = idStr(a && (a.id || a.Id));
          if (id) await safe(call(`${CONTENT}/assets/${encodeURIComponent(id)}/edits`, {
            method: "POST",
            body: {
              action: "clip_render",
              params: JSON.stringify(recipe),
              prompt: lead,
              status: "recipe",
              credits: 0
            }
          }), null);
          toast("Clip ready.");
          if (this.props.onDone) this.props.onDone(id);
        }
      } catch (e) {
        const info = api.errInfo(e);
        if (info.insufficient) {
          if (this._alive) this.setState({
            credits: {
              buyUrl: e && e.data && e.data.buyUrl || BUY_URL
            }
          });
        } else toast(info.message || "Could not start the clip.");
      } finally {
        if (this._alive) this.setState({
          busy: false,
          progress: ""
        });
        if (this.props.onBusy) this.props.onBusy(false);
      }
    });
  }
  isPage() {
    return this.props.layout === "page";
  }
  componentDidMount() {
    // The page shows the starting image straight away (?ref=), so it loads the asset list itself; the pickers reuse it.
    if (this.isPage()) api.listAssets({
      $top: 400
    }).then(list => {
      if (this._alive && !this.state.assetsLoaded) this.setState({
        assets: list || [],
        assetsLoaded: true
      });
    }, () => {
      if (this._alive) this.setState({
        assetsLoaded: true
      });
    });
  }
  componentDidUpdate() {
    // Square is 8 s only: on the page the length grows with the lines, so a square pick falls back to wide.
    if (this.isPage() && this.state.aspect === "1:1" && this.effLength() > 8) this.setState({
      aspect: "16:9"
    });
  }
  componentWillUnmount() {
    this._alive = false;
  }
  filledLines() {
    return this.state.shots.filter(x => String(x || "").trim()).length;
  }
  autoLength() {
    return lengthForLines(this.filledLines());
  }
  /** Seconds to render: the compact form's Length pick, or on the page the written lines (or "Make it longer"). */
  effLength() {
    return this.isPage() ? Math.max(this.autoLength(), Number(this.state.longer) || 0) : this.state.length;
  }
  seg(list, value, onChange, disabled) {
    return /*#__PURE__*/React.createElement("span", {
      className: "hub-ship-seg"
    }, list.map(([k, l, title]) => /*#__PURE__*/React.createElement("button", {
      key: String(k),
      type: "button",
      title: title || "",
      className: "territory-toggle xs" + (value === k ? " active" : ""),
      disabled: disabled,
      onClick: () => onChange(k)
    }, l)));
  }
  renderPage() {
    const s = this.state;
    const length = this.effLength();
    return /*#__PURE__*/React.createElement(ClipNewDirect, {
      s: s,
      imageUrl: this.assetUrl(s.refId),
      lines: this.filledLines(),
      length: length,
      autoLength: this.autoLength(),
      n: shotsFor(length),
      maxLines: MAX_SHOT_LINES,
      maxKeep: MAX_KEEP,
      placeholders: SHOT_PLACEHOLDERS,
      assetUrl: this.assetUrl,
      set: patch => this.setState(patch),
      setQuality: this.setQuality,
      setShot: this.setShot,
      addShot: this.addShot,
      removeShot: this.removeShot,
      setRef: id => this.setState({
        refId: idStr(id),
        keepIds: s.keepIds.filter(x => x !== idStr(id))
      }),
      addKeep: id => {
        if (s.keepIds.length < MAX_KEEP && !s.keepIds.includes(idStr(id)) && idStr(id) !== s.refId) this.setState({
          keepIds: [...s.keepIds, idStr(id)]
        });
      },
      removeKeep: id => this.setState({
        keepIds: s.keepIds.filter(x => x !== id)
      }),
      make: this.make,
      onBack: this.props.onBack || this.props.onCancel,
      onUploadMode: this.props.onUploadMode
    });
  }
  render() {
    if (this.isPage()) return this.renderPage();
    const s = this.state;
    const busy = s.busy;
    const n = shotsFor(s.length);
    return /*#__PURE__*/React.createElement("div", {
      className: "vw-form"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-create-cols"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-create-panel"
    }, !this.props.versionOf ? /*#__PURE__*/React.createElement("label", {
      className: "hub-bld-field block",
      style: {
        marginTop: 0
      }
    }, "Title ", /*#__PURE__*/React.createElement("i", null, "optional"), /*#__PURE__*/React.createElement("input", {
      value: s.title,
      disabled: busy,
      placeholder: "Written from the first direction line when empty",
      onChange: e => this.setState({
        title: e.target.value
      }),
      style: {
        ...INPUT,
        height: 32,
        padding: "0 9px",
        marginTop: 4
      }
    })) : null, /*#__PURE__*/React.createElement("div", {
      className: "hub-create-row"
    }, /*#__PURE__*/React.createElement("span", {
      className: "k"
    }, "Length"), this.seg(CLIP_LENGTHS.map(x => [x, `${x} s`]), s.length, this.setLength, busy)), /*#__PURE__*/React.createElement("div", {
      className: "hub-create-row"
    }, /*#__PURE__*/React.createElement("span", {
      className: "k"
    }, "Quality"), this.seg(VIDEO_QUALITIES.map(([k, l, t]) => [k, l, t]), s.quality, this.setQuality, busy)), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "2px 0 6px"
      }
    }, qualityNote(s.quality), "."), /*#__PURE__*/React.createElement("div", {
      className: "hub-create-row"
    }, /*#__PURE__*/React.createElement("span", {
      className: "k"
    }, "Format"), this.seg([["16:9", "16:9"], ["9:16", "9:16"], ["1:1", "1:1", "Square only at 8 s: the extension shots render in 16:9 or 9:16."]], s.aspect, v => {
      if (v === "1:1" && s.length > 8) {
        toast("Square is 8 seconds only.");
        return;
      }
      this.setState({
        aspect: v
      });
    }, busy)), /*#__PURE__*/React.createElement("div", {
      className: "k",
      style: {
        margin: "12px 0 4px"
      }
    }, "Shots ", /*#__PURE__*/React.createElement("i", null, n > 1 ? `direction per shot, in order · up to ${MAX_SHOT_LINES} lines` : "one direction line")), /*#__PURE__*/React.createElement("div", {
      className: "hub-create-shots"
    }, s.shots.map((line, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "hub-create-shot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, i + 1), /*#__PURE__*/React.createElement("input", {
      value: line,
      disabled: busy,
      placeholder: SHOT_PLACEHOLDERS[i % SHOT_PLACEHOLDERS.length],
      onChange: e => this.setShot(i, e.target.value),
      style: {
        ...INPUT,
        height: 30,
        padding: "0 8px",
        marginTop: 0
      }
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle xs",
      disabled: busy || s.shots.length <= 1,
      title: "Remove this line",
      onClick: () => this.removeShot(i)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      style: {
        fontSize: 14
      }
    }, "close")))), s.shots.length < MAX_SHOT_LINES ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle xs add",
      disabled: busy,
      onClick: this.addShot
    }, "+ Add a shot line") : null), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "10px 0 0"
      }
    }, "The first line drives the opening eight seconds; each next line drives one seven-second extension in order. With fewer lines than shots the last line repeats. Takes a few minutes per shot.")), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: "k",
      style: {
        margin: "2px 0 6px"
      }
    }, "Product image ", /*#__PURE__*/React.createElement("i", null, "required, drives the first frame")), /*#__PURE__*/React.createElement(AssetPicker, {
      compact: true,
      single: true,
      only: ["product", "image", "render"],
      accept: "image",
      picks: s.refId ? [s.refId] : [],
      disabled: busy,
      onAssets: list => this.setState({
        assets: list
      }),
      onChange: ids => this.setState({
        refId: ids[0] || "",
        keepIds: s.keepIds.filter(x => x !== ids[0])
      })
    }), /*#__PURE__*/React.createElement("div", {
      className: "k",
      style: {
        margin: "12px 0 6px"
      }
    }, "Keep consistent ", /*#__PURE__*/React.createElement("i", null, s.keepIds.length ? `${s.keepIds.length} of ${MAX_KEEP} picked` : `optional, up to ${MAX_KEEP} images that keep the product consistent`)), /*#__PURE__*/React.createElement(AssetPicker, {
      compact: true,
      only: ["product", "image", "render"],
      accept: "image",
      max: MAX_KEEP,
      exclude: s.refId ? [s.refId] : [],
      picks: s.keepIds,
      disabled: busy,
      onAssets: list => this.setState({
        assets: list
      }),
      onChange: ids => this.setState({
        keepIds: ids.slice(0, MAX_KEEP)
      })
    }))), s.credits ? /*#__PURE__*/React.createElement("div", {
      className: "sq-notice",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "info"), /*#__PURE__*/React.createElement("span", null, "Not enough credits for this clip."), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      size: "small",
      onClick: () => window.open(s.credits.buyUrl, "_blank", "noopener")
    }, "Buy credits")) : null, /*#__PURE__*/React.createElement("div", {
      className: "hub-create-foot right"
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0,
        flex: 1
      }
    }, busy ? (s.progress ? `Rendering · ${s.progress}.` : "Rendering.") + (s.pendingId ? " You can leave this page." : " Keep this page open.") : this.props.versionOf ? "The new render is saved as a version; the earlier ones stay." : ""), this.props.onCancel ? /*#__PURE__*/React.createElement(Button, {
      disabled: busy,
      onClick: this.props.onCancel
    }, "Cancel") : null, /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: busy || !(s.refId || this.props.recipe && this.props.recipe.imageUrl),
      onClick: this.make
    }, busy ? "Rendering…" : n > 1 ? `Make the clip · ${n} shots` : "Make the clip")));
  }
}
class ClipWorkspace extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      family: undefined,
      edits: [],
      sel: "",
      upPct: 0,
      upBusy: false,
      err: ""
    });
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "load", async selectId => {
      const id = this.id();
      try {
        const [v, e] = await Promise.all([call(`${CONTENT}/assets/${encodeURIComponent(id)}/versions`), safe(call(`${CONTENT}/assets/${encodeURIComponent(id)}/edits`, {
          query: {
            $top: 100
          }
        }), null)]);
        const family = items(v).map(norm).filter(Boolean).sort((a, b) => a.version - b.version);
        if (!this._alive) return;
        if (!family.length || family[0].kind !== "video") {
          this.setState({
            family: null,
            err: family.length ? "This asset is not a video. Images open in the image workspace." : "This video was not found. It may be in the Trash."
          });
          return;
        }
        const want = idStr(selectId || this.state.sel);
        const sel = family.find(x => x.id === want) ? want : family[family.length - 1].id;
        this.setState({
          family,
          edits: items(e),
          sel
        });
      } catch (er) {
        if (this._alive) this.setState({
          family: null,
          err: er && er.message || "The video could not be loaded."
        });
      }
    });
    _defineProperty(this, "upload", async file => {
      if (!file) return;
      if (!/^video\//.test(file.type || "")) {
        toast("Pick a video file: MP4, MOV or WEBM.");
        return;
      }
      this.setState({
        upBusy: true,
        upPct: 0
      });
      try {
        const a = await api.uploadAsset(file, p => {
          if (this._alive) this.setState({
            upPct: p
          });
        });
        if (a && a.id) this.props.history.replace(`/Content/clips/${a.id}`);else toast("The upload did not return a video.");
      } catch (e) {
        toast(e && e.message || "Upload failed.");
      } finally {
        if (this._alive) this.setState({
          upBusy: false
        });
      }
    });
  }
  id() {
    const id = this.props.params && this.props.params.id;
    return id && id !== "new" ? decodeURIComponent(id) : "";
  }
  isNew() {
    return !this.id();
  }
  componentDidMount() {
    if (!this.isNew()) this.load();
    try {
      window.scrollTo(0, 0);
    } catch (e) {/* no window */}
  }
  componentDidUpdate(prev) {
    if ((prev.params && prev.params.id) !== (this.props.params && this.props.params.id)) {
      this.setState({
        family: undefined,
        edits: [],
        sel: ""
      });
      if (!this.isNew()) this.load();
    }
  }
  componentWillUnmount() {
    this._alive = false;
  }
  selected() {
    const f = this.state.family || [];
    return f.find(x => x.id === this.state.sel) || f[f.length - 1] || null;
  }
  recipe() {
    const hit = (this.state.edits || []).find(j => (j.action || j.Action) === "clip_render" && (j.params || j.Params));
    return hit ? parseJson(hit.params || hit.Params) : null;
  }
  /**
   * One row per version (newest first), described by the edit that made it when the edit log has one, otherwise by
   * where the version came from (sequence renders and older tools save versions without an edit row), plus edits that
   * produced nothing (failed). The original's directed-clip recipe shows as a chip on the original's row.
   */
  historyRows(family, edits) {
    const jobs = (edits || []).map(j => ({
      action: j.action || j.Action,
      prompt: j.prompt || j.Prompt,
      status: String(j.status || j.Status || ""),
      out: idStr(j.outputAssetId || j.OutputAssetId),
      by: j.createdBy || j.CreatedBy,
      when: j.createdDate || j.CreatedDate,
      id: idStr(j.id || j.Id)
    }));
    const recipe = jobs.find(j => j.status === "recipe");
    const origin = x => {
      const src = String(x.source || "").toLowerCase();
      if (x.version <= 1) return src === "upload" ? "Uploaded" : src === "plunk_cinematic" ? "Directed clip" : src === "plunk_sequence" ? "Sequence render" : src === "plunk_video" ? "Rendered video" : "Original";
      return src === "plunk_sequence" ? "Sequence render" : "Saved version";
    };
    const rows = family.map(x => {
      const j = jobs.find(k => k.out && k.out === x.id);
      const vl = x.version <= 1 ? "Original" : `v${x.version}`;
      if (j) return {
        key: "v" + x.id,
        versionId: x.id,
        vl,
        label: actionLabel(j.action),
        detail: j.prompt || x.title,
        by: j.by,
        when: j.when || x.createdDate,
        status: j.status
      };
      const isRoot = x.version <= 1;
      return {
        key: "v" + x.id,
        versionId: x.id,
        vl,
        label: origin(x),
        detail: isRoot ? "" : x.title,
        by: isRoot && recipe ? recipe.by : "",
        when: x.createdDate,
        directed: isRoot && !!recipe
      };
    });
    jobs.filter(j => !j.out && j.status === "failed").forEach(j => rows.push({
      key: "j" + j.id,
      versionId: "",
      vl: "",
      label: actionLabel(j.action),
      detail: j.prompt,
      by: j.by,
      when: j.when,
      status: "failed"
    }));
    return rows.sort((a, b) => new Date(b.when || 0) - new Date(a.when || 0));
  }
  renderNew() {
    const q = (() => {
      try {
        return new URLSearchParams(window.location.search);
      } catch (e) {
        return null;
      }
    })();
    const mode = q && q.get("mode") || "direct";
    const ref = q && q.get("ref") || "";
    const {
      upBusy,
      upPct
    } = this.state;
    const back = () => this.props.history.push("/Content");
    if (mode === "upload") {
      return /*#__PURE__*/React.createElement(ClipNewUpload, {
        busy: upBusy,
        pct: upPct,
        onFile: this.upload,
        onBack: back,
        onDirectMode: () => this.props.history.replace("/Content/clips/new?mode=direct" + (ref ? `&ref=${encodeURIComponent(ref)}` : ""))
      });
    }
    return /*#__PURE__*/React.createElement(ClipForm, {
      layout: "page",
      refId: ref,
      onDone: id => {
        if (id) this.props.history.replace(`/Content/clips/${id}`);
      },
      onBack: back,
      onUploadMode: () => this.props.history.replace("/Content/clips/new?mode=upload" + (ref ? `&ref=${encodeURIComponent(ref)}` : ""))
    });
  }
  render() {
    if (this.isNew()) return this.renderNew();
    const {
      family,
      edits,
      err
    } = this.state;
    if (family === undefined) return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view content-view vw-page cx-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, "Content \xB7 Clips"),
      title: "Clip"
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-card vw-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "territory-hint"
    }, "Loading the video\u2026")));
    if (family === null) return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view content-view vw-page cx-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("a", {
        onClick: () => this.props.history.push("/Content")
      }, "Content"), " \xB7 Clips"),
      title: "Clip"
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-card vw-card"
    }, /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, err)));
    const asset = this.selected();
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view content-view cx-view"
    }, /*#__PURE__*/React.createElement(ClipEditor, {
      family: family,
      edits: edits,
      asset: asset,
      hist: this.historyRows(family, edits),
      recipe: this.recipe(),
      ClipForm: ClipForm,
      history: this.props.history,
      onSelect: id => this.setState({
        sel: id
      }),
      onReload: id => this.load(id || null)
    }));
  }
}
export default withRouter(ClipWorkspace);